import React, { useState, useEffect, useCallback, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Payments.css";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import api from "../../axiosInstance";
import { useApp } from "../../AppProvider";
import { useAuth } from "../../AuthProvider";
import EditBookedAppointment from "../../Components/Appointment/EditBookedAppointment";
import logger from "../../utils/logger";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const localDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const todayStr = () => localDate(new Date());
const startOf = (unit) => {
    const d = new Date();
    if (unit === "month")   return localDate(new Date(d.getFullYear(), d.getMonth(), 1));
    if (unit === "week")    { d.setDate(d.getDate() - d.getDay()); return localDate(d); }
    if (unit === "quarter") { const q = Math.floor(d.getMonth() / 3); return localDate(new Date(d.getFullYear(), q * 3, 1)); }
    if (unit === "year")    return localDate(new Date(d.getFullYear(), 0, 1));
    return todayStr();
};
const INR = (n) => n == null ? "—" : "₹" + Math.round(n).toLocaleString("en-IN");
const INRd = (n) => n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtName = (n) => {
    if (!n) return "—";
    if (typeof n === "string") return n;
    return [n.firstName, n.lastName].filter(Boolean).join(" ") || "—";
};
const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return `${dt.toLocaleString("default", { month: "short" })} ${dt.getDate()}`;
};

const CHIP_COLORS = ["#1d9e75", "#378add", "#d4537e", "#9b59b6", "#e67e22"];
const ICON_COLORS = {
    green: { bg: "#dcfce7", stroke: "#16a34a" },
    amber: { bg: "#fef3c7", stroke: "#b45309" },
    red: { bg: "#fee2e2", stroke: "#b91c1c" },
    blue: { bg: "#dbeafe", stroke: "#1d4ed8" },
};
const SPLIT_COLORS = { Cash: "#1d9e75", UPI: "#378add", Insurance: "#f5c842", Card: "#d4537e", Other: "#9b59b6" };

// ─── Shared popup styles (mirrors EditBookedAppointment S object) ──────────────
const PILL_COLORS = {
    "Pending": { background: "#fef3c7", color: "#92400e" },
    "Partially Paid": { background: "#dbeafe", color: "#1e40af" },
    "Paid": { background: "#dcfce7", color: "#166534" },
    "Cancelled": { background: "#f3f4f6", color: "#6b7280" },
    "Overdue": { background: "#fee2e2", color: "#991b1b" },
};
const PS = {
    backdrop: { position: "fixed", inset: 0, background: "rgba(26,42,58,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'DM Sans',system-ui,sans-serif" },
    modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, overflow: "hidden", maxHeight: "92vh", overflowY: "auto" },
    modalSm: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, overflow: "hidden", maxHeight: "92vh", overflowY: "auto" },
    mhead: { background: "#1a2a3a", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    mheadTitle: { color: "#fff", fontSize: 15, fontWeight: 600 },
    mheadSub: { color: "#8899aa", fontSize: 11, marginTop: 2 },
    closeBtn: { width: 28, height: 28, border: "0.5px solid #374151", borderRadius: 7, background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#8899aa" },
    pill: { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 },
    mbody: { padding: "20px 22px" },
    mfoot: { padding: "14px 22px", borderTop: "0.5px solid #e8eaed", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
    sectionTitle: { fontSize: 11, fontWeight: 500, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 10 },
    formGroup: { display: "flex", flexDirection: "column", gap: 4 },
    flbl: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em" },
    finp: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
    fsel: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%" },
    invSkus: { border: "0.5px solid #e8eaed", borderRadius: 10, overflow: "hidden", marginBottom: 14 },
    invHdr: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", background: "#f9fafb", borderBottom: "0.5px solid #e8eaed" },
    invHdrCell: { fontSize: 10, color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: ".04em", padding: "8px 10px" },
    skuRow: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", borderBottom: "0.5px solid #f5f5f5", alignItems: "center" },
    skuCell: { padding: "8px 10px", fontSize: 12, color: "#1a2a3a" },
    skuName: { fontSize: 12, fontWeight: 500, color: "#1a2a3a" },
    skuCat: { fontSize: 10, color: "#9ca3af" },
    numInp: { height: 28, border: "0.5px solid #e8eaed", borderRadius: 5, padding: "0 6px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", textAlign: "center" },
    delBtn: { width: 22, height: 22, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 },
    addSkuRow: { padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, borderTop: "0.5px solid #e8eaed", background: "#fafafa" },
    addSkuSel: { flex: 1, height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", fontFamily: "inherit" },
    btnAddSku: { height: 30, padding: "0 12px", border: "0.5px solid #1d9e75", borderRadius: 6, background: "#e1f5ee", color: "#0f6e56", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
    discBox: { border: "0.5px solid #e8eaed", borderRadius: 10, padding: 12 },
    discBoxTitle: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 },
    discInp: { height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit" },
    togTrack: { width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", position: "relative", transition: "background .2s" },
    togThumb: { position: "absolute", top: 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s" },
    totalsBox: { background: "#f9fafb", border: "0.5px solid #e8eaed", borderRadius: 10, padding: "12px 14px" },
    totRow: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151", padding: "3px 0" },
    totRowGrand: { borderTop: "0.5px solid #e8eaed", marginTop: 6, paddingTop: 8, fontSize: 14, fontWeight: 600, color: "#1a2a3a" },
    historyRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, padding: "7px 0", borderBottom: "0.5px solid #f5f5f5", fontSize: 12 },
    modeBtn: { height: 28, border: "0.5px solid #e8eaed", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff", color: "#6b7280" },
    modeBtnOn: { background: "#1a2a3a", color: "#fff", borderColor: "#1a2a3a" },
    btnBack: { height: 36, padding: "0 18px", border: "0.5px solid #e8eaed", borderRadius: 8, background: "#fff", fontSize: 13, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
    btnNext: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1d9e75", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
    btnConfirm: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1a2a3a", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
};

// ─── CSkuRow (mirrors SkuRow in EditBookedAppointment) ────────────────────────
function CSkuRow({ item, index, onQtyChange, onDiscChange, onRemove, disabled }) {
    const gross = item.unitPrice * item.qty;
    const disc = gross * (item.lineDisc || 0) / 100;
    const after = gross - disc;
    const taxRate = (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
    const total = after + after * taxRate / 100;
    return (
        <div style={PS.skuRow}>
            <div style={{ padding: "8px 10px" }}>
                <div style={PS.skuName}>{item.name || item.skuId}</div>
                <div style={PS.skuCat}>{item.category}</div>
            </div>
            <div style={PS.skuCell}>{INR(item.unitPrice)}</div>
            <div style={PS.skuCell}>
                <input type="number" min="1" max="99" value={item.qty} disabled={disabled}
                    onChange={e => onQtyChange(index, e.target.value)}
                    style={{ ...PS.numInp, width: 50, opacity: disabled ? 0.5 : 1 }} />
            </div>
            <div style={{ ...PS.skuCell, display: "flex", alignItems: "center", gap: 3 }}>
                <input type="number" min="0" max="100" value={item.lineDisc} disabled={disabled}
                    onChange={e => onDiscChange(index, e.target.value)}
                    style={{ ...PS.numInp, width: 46, opacity: disabled ? 0.5 : 1 }} />
                <span style={{ fontSize: 11, color: "#9ca3af" }}>%</span>
            </div>
            <div style={{ ...PS.skuCell, fontWeight: 500 }}>{INR(total)}</div>
            <div style={{ padding: "0 4px" }}>
                {!disabled && (
                    <button onClick={() => onRemove(index)} style={PS.delBtn}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── CToggle ──────────────────────────────────────────────────────────────────
function CToggle({ on, onToggle }) {
    return (
        <button onClick={onToggle} style={{ ...PS.togTrack, background: on ? "#1d9e75" : "#d1d5db" }}>
            <div style={{ ...PS.togThumb, left: on ? 19 : 3 }} />
        </button>
    );
}

// ─── PatientSearchModal ───────────────────────────────────────────────────────
function PatientSearchModal({ onSelect, onClose }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        if (query.length < 3) { setResults([]); return; }
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await api.get("/search/patients", { params: { searchQuery: query } });
                setResults(res.data || []);
            } catch (err) {
                logger.error("Patient search error:", err);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    return (
        <div style={PS.backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={PS.modalSm}>
                <div style={PS.mhead}>
                    <div>
                        <div style={PS.mheadTitle}>New Invoice</div>
                        <div style={PS.mheadSub}>Search for a patient to bill</div>
                    </div>
                    <button style={PS.closeBtn} onClick={onClose}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div style={PS.mbody}>
                    <div style={PS.formGroup}>
                        <label style={PS.flbl}>Patient</label>
                        <div style={{ position: "relative" }}>
                            <svg style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                                width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input
                                autoFocus
                                type="text"
                                style={{ ...PS.finp, paddingLeft: 28 }}
                                placeholder="Name, UHID or mobile number…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                        </div>
                        {query.length > 0 && query.length < 3 && (
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>Type at least 3 characters</span>
                        )}
                    </div>

                    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                        {searching ? (
                            <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#9ca3af" }}>Searching…</div>
                        ) : results.length === 0 && query.length >= 3 ? (
                            <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#9ca3af" }}>No patients found</div>
                        ) : results.map((p, i) => {
                            const name = [p.name?.firstName, p.name?.middleName, p.name?.lastName].filter(Boolean).join(" ");
                            const initials = (p.name?.firstName?.[0] || "?").toUpperCase();
                            const phone = p.contact?.phoneNumber?.phoneNumber || p.phoneNumber || "";
                            return (
                                <div key={p._id || i} onClick={() => onSelect(p)}
                                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "0.5px solid #e8eaed", borderRadius: 10, cursor: "pointer" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fbfc"}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1d9e75", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                                        {initials}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: "#1a2a3a" }}>{name}</div>
                                        {phone && <div style={{ fontSize: 11, color: "#9ca3af" }}>{phone}</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── CreateInvoiceModal ───────────────────────────────────────────────────────
function CreateInvoiceModal({ patient, onClose, onCreated }) {
    const { user } = useAuth();
    const { selectedClinic } = useApp();

    const patientName = [patient.name?.firstName, patient.name?.middleName, patient.name?.lastName].filter(Boolean).join(" ");

    const [dataLoading, setDataLoading] = useState(true);
    const [skuCatalog, setSkuCatalog] = useState([]);
    const [skuItems, setSkuItems] = useState([]);
    const [skuPickerId, setSkuPickerId] = useState("");
    const [insEnabled, setInsEnabled] = useState(false);
    const [insCompany, setInsCompany] = useState("");
    const [insPolicy, setInsPolicy] = useState("");
    const [insCoverage, setInsCoverage] = useState(0);
    const [savingInvoice, setSavingInvoice] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [payAmount, setPayAmount] = useState("");
    const [payMethod, setPayMethod] = useState("Cash");
    const [payNote, setPayNote] = useState("");
    const [recordingPayment, setRecordingPayment] = useState(false);

    useEffect(() => {
        const clinicId = selectedClinic?._id;
        if (!clinicId) { setDataLoading(false); return; }
        api.get("/clinicSKU", { params: { clinicId, limit: 1000, page: 1 } })
            .then(res => setSkuCatalog((res.data.data || []).filter(s => s.isActive !== false)))
            .catch(err => logger.error("SKU catalog error:", err))
            .finally(() => setDataLoading(false));
    }, [selectedClinic]);

    const totals = useMemo(() => {
        let subTotal = 0, lineDiscTotal = 0, taxTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
        for (const s of skuItems) {
            const gross = s.unitPrice * s.qty;
            const disc = gross * (s.lineDisc || 0) / 100;
            const after = gross - disc;
            subTotal += gross;
            lineDiscTotal += disc;
            cgstTotal += after * (s.cgst || 0) / 100;
            sgstTotal += after * (s.sgst || 0) / 100;
            igstTotal += after * (s.igst || 0) / 100;
            taxTotal += after * ((s.cgst || 0) + (s.sgst || 0) + (s.igst || 0)) / 100;
        }
        const afterLineDsc = subTotal - lineDiscTotal;
        const insAmt = insEnabled ? afterLineDsc * insCoverage / 100 : 0;
        const grand = afterLineDsc - insAmt + taxTotal;
        const paid = invoice?.totalPaidAmount ?? 0;
        const balance = Math.max(0, grand - paid);
        return { subTotal, lineDiscTotal, insAmt, taxTotal, cgstTotal, sgstTotal, igstTotal, grand, paid, balance };
    }, [skuItems, insEnabled, insCoverage, invoice]);

    const addSku = () => {
        if (!skuPickerId) return;
        const sku = skuCatalog.find(s => s.clinicSkuId === skuPickerId);
        if (!sku || skuItems.find(s => s.skuId === sku.clinicSkuId)) { setSkuPickerId(""); return; }
        setSkuItems(prev => [...prev, {
            skuId: sku.clinicSkuId, name: sku.name, category: sku.category || "",
            unitPrice: sku.unitPrice,
            cgst: sku.tax?.cgst ?? 0, sgst: sku.tax?.sgst ?? 0, igst: sku.tax?.igst ?? 0,
            qty: 1, lineDisc: 0,
        }]);
        setSkuPickerId("");
    };
    const removeSku = (i) => setSkuItems(prev => prev.filter((_, idx) => idx !== i));
    const updateQty = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, qty: Math.max(1, parseInt(v) || 1) } : s));
    const updateLineDsc = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, lineDisc: Math.min(100, Math.max(0, parseFloat(v) || 0)) } : s));

    const saveInvoice = async () => {
        if (skuItems.length === 0) return;
        setSavingInvoice(true);
        try {
            const expenseTable = skuItems.map(s => ({ skuId: s.skuId, quantity: s.qty, discount: s.lineDisc || 0 }));
            const body = { expenseTable };
            if (insEnabled) body.insuranceDetails = { insuranceCompany: insCompany, policyNumber: Number(insPolicy) || insPolicy, coveragePercent: insCoverage };

            if (invoice) {
                const res = await api.put(`/medicalExpenditures/${invoice._id}`, body);
                const updated = res.data.data || res.data;
                setInvoice(prev => ({ ...prev, ...updated }));
            } else {
                const dueDate = new Date();
                dueDate.setDate(dueDate.getDate() + 30);
                const res = await api.post("/medicalExpenditures", {
                    clinicId: selectedClinic._id,
                    patientId: patient._id,
                    doctorId: user._id,
                    appointments: [],
                    dueDate: dueDate.toISOString().slice(0, 10),
                    ...body,
                });
                setInvoice(res.data.data || res.data);
            }
        } catch (err) {
            logger.error("Save invoice error:", err);
        } finally {
            setSavingInvoice(false);
        }
    };

    const recordPayment = async () => {
        if (!invoice || !payAmount || parseFloat(payAmount) <= 0) return;
        setRecordingPayment(true);
        try {
            const res = await api.post(`/medicalExpenditures/${invoice._id}/payment`, {
                amount: parseFloat(payAmount), paymentMethod: payMethod,
                ...(payNote ? { note: payNote } : {}),
            });
            const updated = res.data.data || res.data;
            setInvoice(prev => ({ ...prev, ...updated }));
            setPayAmount(""); setPayNote("");
        } catch (err) {
            logger.error("Record payment error:", err);
        } finally {
            setRecordingPayment(false);
        }
    };

    const isBusy = savingInvoice || recordingPayment;
    const isClosed = invoice?.status === "Cancelled";

    return (
        <div style={PS.backdrop} onClick={e => e.target === e.currentTarget && !isBusy && onClose()}>
            <div style={PS.modal}>

                {/* Header */}
                <div style={PS.mhead}>
                    <div>
                        <div style={PS.mheadTitle}>{patientName}</div>
                        <div style={PS.mheadSub}>
                            {invoice
                                ? `Invoice · ${invoice.invoiceNumber || invoice.medicalExpenditureID || ""}`
                                : "New Invoice · No appointment"}
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {invoice && (
                            <span style={{ ...PS.pill, ...(PILL_COLORS[invoice.status] || {}) }}>
                                {invoice.status}
                            </span>
                        )}
                        <button style={PS.closeBtn} onClick={onClose} disabled={isBusy}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={PS.mbody}>
                    {dataLoading ? (
                        <div style={{ textAlign: "center", padding: 40, fontSize: 13, color: "#9ca3af" }}>Loading catalogue…</div>
                    ) : (
                        <>
                            {/* SKU table */}
                            <div style={PS.sectionTitle}>Services &amp; items</div>
                            <div style={PS.invSkus}>
                                <div style={PS.invHdr}>
                                    {["Service / item", "Unit price", "Qty", "Discount %", "Total", ""].map((h, i) => (
                                        <span key={i} style={PS.invHdrCell}>{h}</span>
                                    ))}
                                </div>
                                {skuItems.length === 0
                                    ? <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#9ca3af" }}>No items added yet</div>
                                    : skuItems.map((s, i) => (
                                        <CSkuRow key={s.skuId || i} item={s} index={i}
                                            onQtyChange={updateQty} onDiscChange={updateLineDsc}
                                            onRemove={removeSku} disabled={isClosed} />
                                    ))
                                }
                                {!isClosed && (
                                    <div style={PS.addSkuRow}>
                                        <select value={skuPickerId} onChange={e => setSkuPickerId(e.target.value)} style={PS.addSkuSel}>
                                            <option value="">— Add a service or product —</option>
                                            {skuCatalog
                                                .filter(s => !skuItems.find(x => x.skuId === s.clinicSkuId))
                                                .map(s => (
                                                    <option key={s.clinicSkuId} value={s.clinicSkuId}>
                                                        {s.name} – {INR(s.unitPrice)}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <button onClick={addSku} style={PS.btnAddSku}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ marginRight: 4 }}>
                                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                            </svg>
                                            Add item
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Insurance */}
                            {!isClosed && (
                                <div style={{ ...PS.discBox, marginBottom: 14 }}>
                                    <div style={PS.discBoxTitle}>Insurance</div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                        <span style={{ fontSize: 12, color: "#374151" }}>Apply insurance</span>
                                        <CToggle on={insEnabled} onToggle={() => setInsEnabled(v => !v)} />
                                    </div>
                                    {insEnabled && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            <input value={insCompany} onChange={e => setInsCompany(e.target.value)} placeholder="Insurance company" style={{ ...PS.finp, height: 30, fontSize: 12 }} />
                                            <input value={insPolicy} onChange={e => setInsPolicy(e.target.value)} placeholder="Policy number" style={{ ...PS.finp, height: 30, fontSize: 12 }} />
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <input type="number" min="0" max="100" value={insCoverage}
                                                    onChange={e => setInsCoverage(parseFloat(e.target.value) || 0)}
                                                    style={{ ...PS.discInp, flex: 1 }} placeholder="0" />
                                                <span style={{ fontSize: 12, color: "#6b7280" }}>% covered</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Totals */}
                            <div style={PS.totalsBox}>
                                {[
                                    ["Subtotal", INR(totals.subTotal), null],
                                    ["Line discounts", "−" + INR(totals.lineDiscTotal), "#16a34a"],
                                    ["Insurance coverage", "−" + INR(totals.insAmt), "#16a34a"],
                                ].map(([label, val, col]) => (
                                    <div key={label} style={PS.totRow}>
                                        <span>{label}</span>
                                        <span style={col ? { color: col } : {}}>{val}</span>
                                    </div>
                                ))}
                                {totals.cgstTotal > 0 && <div style={PS.totRow}><span>CGST</span><span>+{INR(totals.cgstTotal)}</span></div>}
                                {totals.sgstTotal > 0 && <div style={PS.totRow}><span>SGST</span><span>+{INR(totals.sgstTotal)}</span></div>}
                                {totals.igstTotal > 0 && <div style={PS.totRow}><span>IGST</span><span>+{INR(totals.igstTotal)}</span></div>}
                                {totals.taxTotal > 0 && (
                                    <div style={{ ...PS.totRow, fontStyle: "italic", color: "#6b7280" }}>
                                        <span>Total tax</span><span>+{INR(totals.taxTotal)}</span>
                                    </div>
                                )}
                                <div style={{ ...PS.totRow, ...PS.totRowGrand }}>
                                    <span>Grand total</span><span>{INRd(totals.grand)}</span>
                                </div>
                                {invoice && (
                                    <>
                                        <div style={{ ...PS.totRow, color: "#16a34a" }}>
                                            <span>Paid</span><span>−{INRd(totals.paid)}</span>
                                        </div>
                                        <div style={{ ...PS.totRow, fontWeight: 600, color: totals.balance > 0 ? "#b91c1c" : "#16a34a" }}>
                                            <span>Balance due</span><span>{INRd(totals.balance)}</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Record payment */}
                            {invoice && !isClosed && (
                                <div style={{ ...PS.discBox, marginTop: 14 }}>
                                    <div style={PS.discBoxTitle}>Record payment</div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                                        <div style={PS.formGroup}>
                                            <label style={PS.flbl}>Amount (₹)</label>
                                            <input type="number" min="0" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="0" style={PS.finp} />
                                        </div>
                                        <div style={PS.formGroup}>
                                            <label style={PS.flbl}>Note (optional)</label>
                                            <input value={payNote} onChange={e => setPayNote(e.target.value)} placeholder="e.g. Partial via GPay" style={PS.finp} />
                                        </div>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 6, marginBottom: 10 }}>
                                        {["Cash", "UPI", "Card", "Insurance", "Other"].map(m => (
                                            <button key={m} onClick={() => setPayMethod(m)}
                                                style={{ ...PS.modeBtn, ...(payMethod === m ? PS.modeBtnOn : {}) }}>
                                                {m}
                                            </button>
                                        ))}
                                    </div>
                                    <button onClick={recordPayment}
                                        disabled={!payAmount || parseFloat(payAmount) <= 0}
                                        style={{ ...PS.btnConfirm, width: "100%", opacity: (!payAmount || parseFloat(payAmount) <= 0) ? 0.5 : 1 }}>
                                        Record payment
                                    </button>
                                </div>
                            )}

                            {/* Payment history */}
                            {invoice?.paymentHistory?.length > 0 && (
                                <div style={{ marginTop: 14 }}>
                                    <div style={PS.sectionTitle}>Payment history</div>
                                    {invoice.paymentHistory.map((p, i) => (
                                        <div key={i} style={PS.historyRow}>
                                            <span style={{ fontWeight: 500, color: "#1a2a3a" }}>{INRd(p.amount)}</span>
                                            <span style={{ color: "#6b7280" }}>{p.paymentMethod}</span>
                                            <span style={{ color: "#9ca3af" }}>
                                                {p.date ? new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                                            </span>
                                            {p.note && <span style={{ color: "#9ca3af", fontStyle: "italic", gridColumn: "1/-1" }}>{p.note}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={PS.mfoot}>
                    <button onClick={() => { onClose(); if (invoice) onCreated(); }} style={PS.btnBack}>
                        {invoice ? "Done" : "Cancel"}
                    </button>
                    {!isClosed && !dataLoading && (
                        <button onClick={saveInvoice} disabled={skuItems.length === 0 || isBusy}
                            style={{ ...PS.btnNext, opacity: (skuItems.length === 0 || isBusy) ? 0.5 : 1 }}>
                            {savingInvoice ? "Saving…" : invoice ? "Update invoice" : "Create invoice"}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
}

// ─── MetricCard ───────────────────────────────────────────────────────────────
function MetricCard({ icon, label, value, change, changeType, loading }) {
    return (
        <div className="payments__metric-card">
            <div className="payments__metric-icon" style={{ background: ICON_COLORS[icon.color]?.bg }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={ICON_COLORS[icon.color]?.stroke}
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {icon.path}
                </svg>
            </div>
            <div className="payments__metric-label">{label}</div>
            <div className="payments__metric-value">
                {loading ? <span className="payments__skeleton">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span> : value}
            </div>
            <div className={`payments__metric-change payments__metric-change--${changeType}`}>
                {loading ? "" : change}
            </div>
        </div>
    );
}

// ─── StatusPill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
    const s = (status || "").toLowerCase().replace(/\s+/g, "-");
    const LABELS = { paid: "Paid", pending: "Pending", "partially-paid": "Partial", cancelled: "Cancelled", overdue: "Overdue" };
    const MODS = { paid: "paid", pending: "pending", "partially-paid": "warn", cancelled: "cancelled", overdue: "overdue" };
    return (
        <span className={`payments__pill payments__pill--${MODS[s] || "pending"}`}>
            {LABELS[s] || status}
        </span>
    );
}

// ─── FilterBar ────────────────────────────────────────────────────────────────
function FilterBar({ dateFrom, dateTo, clinics, selectedClinicIds, onDateFrom, onDateTo, onToggleClinic, onApply, onReset }) {
    const [quick, setQuick] = useState("month");

    const handleQuick = (val) => {
        setQuick(val);
        if (!val) return;
        onDateFrom(val === "today" ? todayStr() : startOf(val));
        onDateTo(todayStr());
    };

    return (
        <div className="payments__filter-bar">
            <div className="payments__filter-group">
                <span className="payments__filter-label">Date Range</span>
                <div className="payments__date-range">
                    <DatePicker
                        selected={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                        onChange={d => { if (d) { onDateFrom(localDate(d)); setQuick(""); } }}
                        dateFormat="dd MMM yyyy"
                        placeholderText="From"
                        className="ov-filter-input ov-datepicker-input"
                        calendarClassName="ov-datepicker-calendar"
                        showPopperArrow={false}
                        maxDate={dateTo ? new Date(dateTo + "T00:00:00") : null}
                    />
                    <span className="payments__date-sep">to</span>
                    <DatePicker
                        selected={dateTo ? new Date(dateTo + "T00:00:00") : null}
                        onChange={d => { if (d) { onDateTo(localDate(d)); setQuick(""); } }}
                        dateFormat="dd MMM yyyy"
                        placeholderText="To"
                        className="ov-filter-input ov-datepicker-input"
                        calendarClassName="ov-datepicker-calendar"
                        showPopperArrow={false}
                        minDate={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                    />
                </div>
                <select className="payments__filter-select" value={quick} onChange={e => handleQuick(e.target.value)}>
                    <option value="">Quick select</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                    <option value="year">This Year</option>
                </select>
            </div>

            <div className="payments__filter-divider" />

            {/* <div className="payments__filter-group">
                <span className="payments__filter-label">Clinic</span>
                <div className="payments__clinic-chips">
                    <button
                        onClick={() => onToggleClinic("all")}
                        className={`payments__clinic-chip${selectedClinicIds.includes("all") ? " payments__clinic-chip--active" : ""}`}
                    >
                        <span className="payments__clinic-dot" style={{ background: "#f5c842" }} />
                        All Clinics
                    </button>
                    {clinics.map((c, i) => (
                        <button
                            key={c._id}
                            onClick={() => onToggleClinic(c._id)}
                            className={`payments__clinic-chip${selectedClinicIds.includes(c._id) ? " payments__clinic-chip--active" : ""}`}
                        >
                            <span className="payments__clinic-dot" style={{ background: CHIP_COLORS[i % CHIP_COLORS.length] }} />
                            {c.name?.fullName || c.name?.shortName || c._id}
                        </button>
                    ))}
                </div>
            </div> */}

            <div className="payments__filter-divider" />
            <button className="payments__btn-apply" onClick={onApply}>Apply Filters</button>
            <button className="payments__btn-reset" onClick={onReset}>Reset</button>
        </div>
    );
}

// ─── AllInvoices ──────────────────────────────────────────────────────────────
function AllInvoices({ clinics, onBack }) {
    const { appointments } = useApp();

    const [patientQuery, setPatientQuery] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [patientSearching, setPatientSearching] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState(startOf("month"));
    const [dateTo, setDateTo] = useState(todayStr());
    const [selClinicIds, setSelClinicIds] = useState(["all"]);
    const [quick, setQuick] = useState("month");
    const [page, setPage] = useState(1);
    const LIMIT = 20;

    const [applied, setApplied] = useState({
        patientId: null, status: "all",
        from: startOf("month"), to: todayStr(), clinics: [],
    });

    const [invoices, setInvoices] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // editTask is the `task` object consumed by EditBookedAppointment
    const [editTask, setEditTask] = useState(null);
    const [editLoading, setEditLoading] = useState(null); // stores invoice._id being loaded

    // Create invoice flow: "search" → pick patient → "invoice" → create
    const [createStep, setCreateStep] = useState(null); // null | "search" | "invoice"
    const [createPatient, setCreatePatient] = useState(null);

    const fetchInvoices = useCallback(async (params) => {
        setLoading(true);
        try {
            const res = await api.get("/medicalExpenditures", { params });
            // API returns { page, limit, totalCount, data: [...] }
            const rows = res.data.data;
            setInvoices(Array.isArray(rows) ? rows : []);
            setTotal(res.data.totalCount ?? 0);
        } catch (err) {
            logger.error("Invoices fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced patient search
    useEffect(() => {
        if (patientQuery.length < 3 || selectedPatient) {
            setPatientResults([]);
            return;
        }
        setPatientSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await api.get("/search/patients", { params: { searchQuery: patientQuery } });
                setPatientResults(res.data || []);
            } catch (err) {
                logger.error("Patient search error:", err);
            } finally {
                setPatientSearching(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [patientQuery, selectedPatient]);

    useEffect(() => {
        const p = { page, limit: LIMIT };
        if (applied.from) p.from = applied.from;
        if (applied.to) p.to = applied.to;
        if (applied.patientId) p.patientId = applied.patientId;
        if (applied.status !== "all") p.status = applied.status;
        if (applied.clinics.length > 0) p.clinicId = applied.clinics.join(",");
        fetchInvoices(p);
    }, [applied, page, fetchInvoices, refreshKey]);

    const openEdit = async (inv) => {
        setEditLoading(inv._id);
        try {
            const res = await api.get(`/medicalExpenditures/${inv._id}`);
            const full = res.data.data || {};

            // Resolve appointment ID (may be string or populated object)
            const rawApptId = Array.isArray(full.appointments) && full.appointments[0];
            const apptId = rawApptId
                ? (typeof rawApptId === "object" ? String(rawApptId._id || rawApptId) : String(rawApptId))
                : null;

            const appt = apptId
                ? appointments.find(a => a._id.toString() === apptId)
                : null;

            // patientId may be a populated object or a plain string
            const patientId = full.patientId
                ? (typeof full.patientId === "object" ? full.patientId._id || full.patientId : full.patientId)
                : null;

            setEditTask({
                id: apptId || "no-appt",
                medicalExpenditureId: inv._id,
                patientId: patientId,
                content: typeof inv.patientId === "object"
                    ? fmtName(inv.patientId?.name)
                    : (inv.patientName || "—"),
                time: appt
                    ? new Date(appt.dateOfAppointment).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                    : "—",
            });
        } catch (err) {
            logger.error("openEdit error:", err);
        } finally {
            setEditLoading(null);
        }
    };

    const handleQuick = (val) => {
        setQuick(val);
        if (!val) return;
        setDateFrom(val === "today" ? todayStr() : startOf(val));
        setDateTo(todayStr());
    };

    const toggleClinic = (id) => {
        if (id === "all") {
            setSelClinicIds(["all"]);
        } else {
            setSelClinicIds(prev => {
                const without = prev.filter(c => c !== "all");
                const next = without.includes(id) ? without.filter(c => c !== id) : [...without, id];
                return next.length === 0 ? ["all"] : next;
            });
        }
    };

    const applyFilters = () => {
        setPage(1);
        setApplied({
            patientId: selectedPatient?._id || null,
            status: statusFilter,
            from: dateFrom, to: dateTo,
            clinics: selClinicIds.includes("all") ? [] : selClinicIds,
        });
    };

    const resetFilters = () => {
        const from = startOf("month"), to = todayStr();
        setPatientQuery(""); setSelectedPatient(null); setPatientResults([]);
        setStatusFilter("all");
        setDateFrom(from); setDateTo(to);
        setSelClinicIds(["all"]); setQuick("month"); setPage(1);
        setApplied({ patientId: null, status: "all", from, to, clinics: [] });
    };


    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    return (
        <div className="inv__page">
            {/* Header */}
            <div className="inv__header">
                <button className="inv__back-btn" onClick={onBack}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back
                </button>
                <h1 className="inv__title">All Invoices</h1>
                {total > 0 && <span className="inv__count-badge">{total} invoices</span>}
                <button className="inv__create-btn" onClick={() => setCreateStep("search")}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create Invoice
                </button>
            </div>

            {/* Filter Bar */}
            <div className="payments__filter-bar inv__filter-bar">
                <div className="payments__filter-group inv__patient-filter-group">
                    <span className="payments__filter-label">Patient</span>
                    <div className="inv__patient-autocomplete">
                        {selectedPatient ? (
                            <div className="inv__patient-chip">
                                <span>{fmtName(selectedPatient.name)}</span>
                                <button className="inv__patient-chip-clear"
                                    onClick={() => { setSelectedPatient(null); setPatientQuery(""); }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="inv__search-wrap">
                                    <svg className="inv__search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input
                                        type="text"
                                        className="inv__search-input"
                                        placeholder="Search patient…"
                                        value={patientQuery}
                                        onChange={e => setPatientQuery(e.target.value)}
                                    />
                                </div>
                                {(patientSearching || patientResults.length > 0) && (
                                    <div className="inv__patient-dropdown">
                                        {patientSearching ? (
                                            <div className="inv__patient-opt inv__patient-opt--muted">Searching…</div>
                                        ) : patientResults.map((p, i) => (
                                            <div key={p._id || i} className="inv__patient-opt"
                                                onClick={() => { setSelectedPatient(p); setPatientQuery(""); setPatientResults([]); }}>
                                                <span className="inv__patient-opt-name">{fmtName(p.name)}</span>
                                                <span className="inv__patient-opt-phone">
                                                    {p.contact?.phoneNumber?.phoneNumber || ""}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="payments__filter-divider" />

                <div className="payments__filter-group">
                    <span className="payments__filter-label">Status</span>
                    <select className="payments__filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="all">All Statuses</option>
                        <option value="Paid">Paid</option>
                        <option value="Pending">Pending</option>
                        <option value="Partially Paid">Partial</option>
                        <option value="Overdue">Overdue</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                </div>

                <div className="payments__filter-divider" />

                <div className="payments__filter-group">
                    <span className="payments__filter-label">Date Range</span>
                    <div className="payments__date-range">
                        <DatePicker
                            selected={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                            onChange={d => { if (d) { setDateFrom(localDate(d)); setQuick(""); } }}
                            dateFormat="dd MMM yyyy"
                            placeholderText="From"
                            className="ov-filter-input ov-datepicker-input"
                            calendarClassName="ov-datepicker-calendar"
                            showPopperArrow={false}
                            maxDate={dateTo ? new Date(dateTo + "T00:00:00") : null}
                        />
                        <span className="payments__date-sep">to</span>
                        <DatePicker
                            selected={dateTo ? new Date(dateTo + "T00:00:00") : null}
                            onChange={d => { if (d) { setDateTo(localDate(d)); setQuick(""); } }}
                            dateFormat="dd MMM yyyy"
                            placeholderText="To"
                            className="ov-filter-input ov-datepicker-input"
                            calendarClassName="ov-datepicker-calendar"
                            showPopperArrow={false}
                            minDate={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                        />
                    </div>
                    <select className="payments__filter-select" value={quick} onChange={e => handleQuick(e.target.value)}>
                        <option value="">Quick select</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="quarter">This Quarter</option>
                        <option value="year">This Year</option>
                    </select>
                </div>

                {/* <div className="payments__filter-divider" />

                <div className="payments__filter-group">
                    <span className="payments__filter-label">Clinic</span>
                    <div className="payments__clinic-chips">
                        <button
                            onClick={() => toggleClinic("all")}
                            className={`payments__clinic-chip${selClinicIds.includes("all") ? " payments__clinic-chip--active" : ""}`}
                        >
                            <span className="payments__clinic-dot" style={{ background: "#f5c842" }} />All Clinics
                        </button>
                        {(clinics || []).map((c, i) => (
                            <button
                                key={c._id}
                                onClick={() => toggleClinic(c._id)}
                                className={`payments__clinic-chip${selClinicIds.includes(c._id) ? " payments__clinic-chip--active" : ""}`}
                            >
                                <span className="payments__clinic-dot" style={{ background: CHIP_COLORS[i % CHIP_COLORS.length] }} />
                                {c.name?.fullName || c.name?.shortName || c._id}
                            </button>
                        ))}
                    </div>
                </div> */}

                <div className="payments__filter-divider" />
                <button className="payments__btn-apply" onClick={applyFilters}>Apply</button>
                <button className="payments__btn-reset" onClick={resetFilters}>Reset</button>
            </div>

            {/* Table */}
            <div className="inv__table-card">
                <table className="payments__table">
                    <thead>
                        <tr>
                            {["Patient", "Date", "Amount", "Status", "Paid", ""].map(h => (
                                <th key={h} className="payments__th">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="payments__td payments__td--empty">Loading…</td></tr>
                        ) : invoices.length === 0 ? (
                            <tr><td colSpan={6} className="payments__td payments__td--empty">No invoices found for this filter</td></tr>
                        ) : invoices.map((inv, i) => (
                            <tr key={inv._id || i} className="inv__table-row">
                                <td className="payments__td">{fmtName(inv.patientId?.name)}</td>
                                <td className="payments__td">{fmtDate(inv.date || inv.createdAt)}</td>
                                <td className="payments__td">{INR(inv.grandTotal)}</td>
                                <td className="payments__td"><StatusPill status={inv.status} /></td>
                                <td className="payments__td">{INR(inv.totalPaidAmount)}</td>
                                <td className="payments__td">
                                    <button className="inv__edit-btn" onClick={() => openEdit(inv)} disabled={editLoading === inv._id}>
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="inv__pagination">
                    <button className="inv__page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <span className="inv__page-info">Page {page} of {totalPages}</span>
                    <button className="inv__page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </div>
            )}

            {/* Edit Modal — same as appointment popup */}
            {editTask && (
                <EditBookedAppointment
                    task={editTask}
                    setEditModalOpen={(open) => {
                        if (!open) {
                            setEditTask(null);
                            setRefreshKey(k => k + 1);
                        }
                    }}
                />
            )}

            {/* Step 1 — patient search */}
            {createStep === "search" && (
                <PatientSearchModal
                    onClose={() => setCreateStep(null)}
                    onSelect={(patient) => {
                        setCreatePatient(patient);
                        setCreateStep("invoice");
                    }}
                />
            )}

            {/* Step 2 — create invoice */}
            {createStep === "invoice" && createPatient && (
                <CreateInvoiceModal
                    patient={createPatient}
                    onClose={() => { setCreateStep(null); setCreatePatient(null); }}
                    onCreated={() => { setCreateStep(null); setCreatePatient(null); setRefreshKey(k => k + 1); }}
                />
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Payments() {
    const { clinics } = useApp();

    const [view, setView] = useState("dashboard");

    // Filter input state (not committed until Apply)
    const [dateFrom, setDateFrom] = useState(startOf("month"));
    const [dateTo, setDateTo] = useState(todayStr());
    const [selectedClinicIds, setSelectedClinicIds] = useState(["all"]);

    // Committed state that actually drives fetches
    const [appliedFrom, setAppliedFrom] = useState(startOf("month"));
    const [appliedTo, setAppliedTo] = useState(todayStr());
    const [appliedClinics, setAppliedClinics] = useState([]);

    const [revenueKey, setRevenueKey] = useState("year");

    // API data
    const [dashLoading, setDashLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [metrics, setMetrics] = useState(null);
    const [paymentSplit, setPaymentSplit] = useState([]);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [topServices, setTopServices] = useState([]);
    const [chartMonths, setChartMonths] = useState([]);

    const clinicParam = (ids) =>
        ids.length === 0 || ids.includes("all") ? undefined : ids.join(",");

    const fetchDashboard = useCallback(async (from, to, clinicIds) => {
        setDashLoading(true);
        try {
            const params = { from, to };
            const cId = clinicParam(clinicIds);
            if (cId) params.clinicId = cId;
            const res = await api.get("/medicalExpenditures/dashboard", { params });
            const d = res.data.data || {};
            setMetrics(d.metrics || null);
            setPaymentSplit(d.paymentSplit || []);
            setRecentInvoices(d.recentInvoices || []);
            setTopServices(d.topServices || []);
        } catch (err) {
            logger.error("Dashboard fetch error:", err);
        } finally {
            setDashLoading(false);
        }
    }, []);

    const fetchRevenueChart = useCallback(async (period, clinicIds) => {
        setChartLoading(true);
        try {
            const params = { year: new Date().getFullYear(), period };
            const cId = clinicParam(clinicIds);
            if (cId) params.clinicId = cId;
            const res = await api.get("/medicalExpenditures/revenue-chart", { params });
            setChartMonths(res.data.data?.months || []);
        } catch (err) {
            logger.error("Revenue chart fetch error:", err);
        } finally {
            setChartLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard(appliedFrom, appliedTo, appliedClinics);
    }, [appliedFrom, appliedTo, appliedClinics, fetchDashboard]);

    useEffect(() => {
        fetchRevenueChart(revenueKey, appliedClinics);
    }, [revenueKey, appliedClinics, fetchRevenueChart]);

    // ── Filter actions ──
    const toggleClinic = (id) => {
        if (id === "all") {
            setSelectedClinicIds(["all"]);
        } else {
            setSelectedClinicIds(prev => {
                const without = prev.filter(c => c !== "all");
                const next = without.includes(id) ? without.filter(c => c !== id) : [...without, id];
                return next.length === 0 ? ["all"] : next;
            });
        }
    };

    const applyFilters = () => {
        setAppliedFrom(dateFrom);
        setAppliedTo(dateTo);
        setAppliedClinics(selectedClinicIds.includes("all") ? [] : selectedClinicIds);
    };

    const resetFilters = () => {
        const from = startOf("month");
        const to = todayStr();
        setDateFrom(from);
        setDateTo(to);
        setSelectedClinicIds(["all"]);
        setAppliedFrom(from);
        setAppliedTo(to);
        setAppliedClinics([]);
    };

    // ── Revenue chart ──
    const revenueChartData = {
        labels: chartMonths.map(m => m.month),
        datasets: [
            { label: "Collected", data: chartMonths.map(m => m.collected), backgroundColor: "#1d9e75", borderRadius: 4, stack: "a" },
            { label: "Pending", data: chartMonths.map(m => m.pending), backgroundColor: "#fac775", borderRadius: 4, stack: "a" },
        ],
    };
    const revenueChartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { font: { size: 11 }, autoSkip: false } },
            y: { stacked: true, grid: { color: "#f0f0f0" }, ticks: { font: { size: 11 }, callback: v => "₹" + Math.round(v / 1000) + "k" } },
        },
    };

    // ── Payment split chart ──
    const splitColors = paymentSplit.map(s => SPLIT_COLORS[s.method] || "#ccc");
    const splitChartData = {
        labels: paymentSplit.map(s => s.method),
        datasets: [{ data: paymentSplit.map(s => s.percent), backgroundColor: splitColors, borderWidth: 0, hoverOffset: 4 }],
    };
    const splitChartOptions = {
        responsive: true, maintainAspectRatio: false, cutout: "65%",
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ctx.label + ": " + ctx.parsed + "%" } },
        },
    };

    if (view === "invoices") {
        return (
            <div className="payments__main-container">
                <div className="payments__main-wrapper">
                    <main className="payments__content">
                        <AllInvoices clinics={clinics || []} onBack={() => setView("dashboard")} />
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="payments__main-container">
            <div className="payments__main-wrapper">
                <main className="payments__content">

                    {/* Filter Bar */}
                    <FilterBar
                        dateFrom={dateFrom} dateTo={dateTo}
                        clinics={clinics || []}
                        selectedClinicIds={selectedClinicIds}
                        onDateFrom={setDateFrom} onDateTo={setDateTo}
                        onToggleClinic={toggleClinic}
                        onApply={applyFilters} onReset={resetFilters}
                    />

                    {/* Page Header */}
                    <div className="payments__page-header">
                        <h1 className="payments__page-title">Billing Overview</h1>
                        {/* <button className="payments__export-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Export CSV
                        </button> */}
                    </div>

                    {/* Metrics */}
                    <div className="payments__metrics-grid">
                        <MetricCard
                            loading={dashLoading}
                            icon={{ color: "green", path: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /> }}
                            label="Total Revenue"
                            value={INR(metrics?.totalRevenue?.amount)}
                            change={metrics?.totalRevenue?.changePercent != null
                                ? `${metrics.totalRevenue.changePercent > 0 ? "+" : ""}${metrics.totalRevenue.changePercent}% vs last period`
                                : ""}
                            changeType={metrics?.totalRevenue?.changePercent >= 0 ? "up" : "down"}
                        />
                        <MetricCard
                            loading={dashLoading}
                            icon={{ color: "amber", path: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></> }}
                            label="Pending Bills"
                            value={INR(metrics?.pendingBills?.amount)}
                            change={metrics?.pendingBills?.count != null ? `${metrics.pendingBills.count} invoices pending` : ""}
                            changeType="warn"
                        />
                        <MetricCard
                            loading={dashLoading}
                            icon={{ color: "red", path: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></> }}
                            label="Overdue Amount"
                            value={INR(metrics?.overdueAmount?.amount)}
                            change={metrics?.overdueAmount?.count != null ? `${metrics.overdueAmount.count} overdue invoices` : ""}
                            changeType="down"
                        />
                        <MetricCard
                            loading={dashLoading}
                            icon={{ color: "blue", path: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></> }}
                            label="Patients Billed"
                            value={metrics?.patientsBilled?.count != null ? metrics.patientsBilled.count.toLocaleString("en-IN") : "—"}
                            change={metrics?.patientsBilled?.changePercent != null
                                ? `${metrics.patientsBilled.changePercent > 0 ? "+" : ""}${metrics.patientsBilled.changePercent}% vs last period`
                                : ""}
                            changeType={metrics?.patientsBilled?.changePercent >= 0 ? "up" : "down"}
                        />
                    </div>

                    {/* Charts */}
                    <div className="payments__charts-row">
                        <div className="payments__chart-card">
                            <div className="payments__chart-head">
                                <span className="payments__chart-title">Monthly Revenue</span>
                                <select className="payments__chart-filter" value={revenueKey} onChange={e => setRevenueKey(e.target.value)}>
                                    <option value="year">This Year</option>
                                    <option value="q1">Q1</option>
                                    <option value="q2">Q2</option>
                                    <option value="q3">Q3</option>
                                    <option value="q4">Q4</option>
                                </select>
                            </div>
                            <div className="payments__legend-row">
                                {[["#1d9e75", "Collected"], ["#fac775", "Pending"]].map(([c, l]) => (
                                    <div key={l} className="payments__legend-item">
                                        <span className="payments__legend-dot" style={{ background: c }} />{l}
                                    </div>
                                ))}
                            </div>
                            <div className="payments__chart-wrapper">
                                {chartLoading
                                    ? <div className="payments__chart-empty">Loading…</div>
                                    : chartMonths.length === 0
                                        ? <div className="payments__chart-empty">No data for this period</div>
                                        : <Bar data={revenueChartData} options={revenueChartOptions} />
                                }
                            </div>
                        </div>

                        <div className="payments__chart-card">
                            <div className="payments__chart-head">
                                <span className="payments__chart-title">Payment Split</span>
                            </div>
                            <div className="payments__legend-row">
                                {paymentSplit.map((s, i) => (
                                    <div key={s.method} className="payments__legend-item">
                                        <span className="payments__legend-dot" style={{ background: splitColors[i] }} />
                                        {s.method} {s.percent}%
                                    </div>
                                ))}
                            </div>
                            <div className="payments__chart-wrapper payments__chart-wrapper--donut">
                                {dashLoading
                                    ? <div className="payments__chart-empty">Loading…</div>
                                    : paymentSplit.length === 0
                                        ? <div className="payments__chart-empty">No payment data</div>
                                        : <Doughnut data={splitChartData} options={splitChartOptions} />
                                }
                            </div>
                        </div>
                    </div>

                    {/* Tables */}
                    <div className="payments__bottom-row">
                        <div className="payments__table-card">
                            <div className="payments__table-head">
                                <span className="payments__table-title">Recent Invoices</span>
                                <button className="payments__view-all" onClick={() => setView("invoices")}>
                                    View All Invoices →
                                </button>
                            </div>
                            <table className="payments__table">
                                <thead>
                                    <tr>{["Patient", "Date", "Amount", "Status"].map(h => <th key={h} className="payments__th">{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {dashLoading ? (
                                        <tr><td colSpan={4} className="payments__td payments__td--empty">Loading…</td></tr>
                                    ) : recentInvoices.length === 0 ? (
                                        <tr><td colSpan={4} className="payments__td payments__td--empty">No invoices in this period</td></tr>
                                    ) : recentInvoices.map((row, i) => (
                                        <tr key={i}>
                                            <td className="payments__td">{fmtName(row.patientName)}</td>
                                            <td className="payments__td">{fmtDate(row.date || row.createdAt)}</td>
                                            <td className="payments__td">{INR(row.grandTotal)}</td>
                                            <td className="payments__td"><StatusPill status={row.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="payments__table-card">
                            <div className="payments__table-head">
                                <span className="payments__table-title">Top Services by Revenue</span>
                            </div>
                            <table className="payments__table">
                                <thead>
                                    <tr>{["Service", "Patients", "Revenue"].map(h => <th key={h} className="payments__th">{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {dashLoading ? (
                                        <tr><td colSpan={3} className="payments__td payments__td--empty">Loading…</td></tr>
                                    ) : topServices.length === 0 ? (
                                        <tr><td colSpan={3} className="payments__td payments__td--empty">No data</td></tr>
                                    ) : topServices.map((row, i) => (
                                        <tr key={i}>
                                            <td className="payments__td">{row.name || row.serviceName || "—"}</td>
                                            <td className="payments__td">{row.patientCount ?? row.patients ?? "—"}</td>
                                            <td className="payments__td">{INR(row.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </main>
            </div>
        </div>
    );
}
